import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Save,
  Medication,
  CheckCircle,
  History,
} from "@mui/icons-material";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import { extractTreatmentsHistory } from "@/fluxvet/utils/questionnaireResponseHelper";
import { HistoryModal } from "./HistoryModal";
/**
 * Gera um ID único para os itens
 */
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// =============================================================================
// TIPOS - Interfaces
// =============================================================================

interface Treatment {
  id: string;
  name: string;
  dosage: string;
  description: string;
}

interface TreatmentsSectionProps {
  control: any;
  watch: any;
  setValue: any;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente de Tratamentos (Medicamentos)
 * Permite adicionar, editar e remover múltiplos tratamentos
 */
export const TreatmentsSection: React.FC<TreatmentsSectionProps> = ({
  control,
  watch,
  setValue,
}) => {
  // Busca o histórico de QuestionnaireResponse do contexto
  const { historyQuestionnaireResponse } = useVetAttendance();

  // Processa o histórico para extrair tratamentos prescritos
  const treatmentsHistory = useMemo(
    () => extractTreatmentsHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  // Observa a lista de tratamentos do formulário
  const treatments: Treatment[] = watch("treatments") || [];

  // Estado para o formulário
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Treatment, "id">>({
    name: "",
    dosage: "",
    description: "",
  });

  // Estado para controlar o modal de histórico
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
  });

  /**
   * Adiciona novo tratamento à lista
   */
  const handleAdd = () => {
    if (!formData.name.trim() || !formData.dosage.trim()) {
      return; // Validação básica
    }

    const newTreatment: Treatment = {
      id: generateId(),
      ...formData,
    };

    setValue("treatments", [...treatments, newTreatment]);
    resetForm();
  };

  /**
   * Inicia edição de um tratamento existente
   */
  const handleEdit = (treatment: Treatment) => {
    // Não permite editar item já assinado
    if ((treatment as any).signed) {
      alert("Este item já foi assinado digitalmente e não pode ser editado.");
      return;
    }
    
    setEditingId(treatment.id);
    setFormData({
      name: treatment.name,
      dosage: treatment.dosage,
      description: treatment.description,
    });
  };

  /**
   * Salva alterações de um tratamento editado
   */
  const handleSaveEdit = () => {
    if (!formData.name.trim() || !formData.dosage.trim()) {
      return;
    }

    const updatedTreatments = treatments.map((treatment) =>
      treatment.id === editingId
        ? { ...treatment, ...formData }
        : treatment
    );

    setValue("treatments", updatedTreatments);
    resetForm();
  };

  /**
   * Remove um tratamento da lista
   */
  const handleDelete = (id: string) => {
    // Verifica se o item está assinado
    const treatment = treatments.find((t) => t.id === id);
    if (treatment && (treatment as any).signed) {
      alert("Este item já foi assinado digitalmente e não pode ser removido.");
      return;
    }
    
    const filtered = treatments.filter((treatment) => treatment.id !== id);
    setValue("treatments", filtered);
  };

  /**
   * Reseta o formulário
   */
  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: "",
      dosage: "",
      description: "",
    });
  };

  return (
    <>
    <Box>
      {/* Cabeçalho */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
        <Medication sx={{ color: "#00A884" }} />
        <Typography variant="h6" fontWeight={600}>
          Tratamentos
        </Typography>
        {treatments.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            ({treatments.length} {treatments.length === 1 ? "item" : "itens"})
          </Typography>
        )}
          </Stack>
          {treatmentsHistory.length > 0 && (
            <Button
              size="small"
              startIcon={<History />}
              onClick={() => setHistoryModal({ isOpen: true })}
              sx={{
                textTransform: "none",
                fontSize: "0.85rem",
                color: "#666",
                "&:hover": {
                  color: "#00A884",
                  bgcolor: "rgba(0, 168, 132, 0.08)",
                },
              }}
            >
              Ver histórico de tratamentos
            </Button>
          )}
      </Stack>

      <Grid container spacing={2}>
        {/* Coluna Esquerda: Formulário (40%) */}
        <Grid item xs={12} md={4.8}>
          <Paper
            key={`treatment-form-${treatments.length}`}
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              bgcolor: "#fafafa",
              height: "100%",
              minHeight: 420,
            }}
          >
            <Stack spacing={2} height="100%">
              <Typography variant="subtitle2" fontWeight={600} color="#00A884">
                {editingId ? "✏️ Editando" : "➕ Novo tratamento"}
              </Typography>
              
              <Divider />

              <TextField
                fullWidth
                label="Nome do medicamento *"
                placeholder="Ex: Amoxicilina"
                value={editingId ? formData.name : undefined}
                defaultValue=""
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fff",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Posologia *"
                placeholder="Ex: 500mg 2x/dia por 7 dias"
                value={editingId ? formData.dosage : undefined}
                defaultValue=""
                onChange={(e) =>
                  setFormData({ ...formData, dosage: e.target.value })
                }
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fff",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Descrição de uso ou observações"
                placeholder="Ex: Administrar após as refeições"
                value={editingId ? formData.description : undefined}
                defaultValue=""
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                multiline
                rows={3}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fff",
                  },
                }}
              />

              <Box sx={{ flexGrow: 1 }} />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {editingId && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    sx={{ textTransform: "none" }}
                  >
                    Cancelar
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={editingId ? <Save /> : <Add />}
                  onClick={editingId ? handleSaveEdit : handleAdd}
                  sx={{
                    bgcolor: "#00A884",
                    "&:hover": { bgcolor: "#008c6e" },
                    textTransform: "none",
                  }}
                >
                  {editingId ? "Salvar" : "Adicionar"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Coluna Direita: Lista de itens (60%) */}
        <Grid item xs={12} md={7.2}>
          {treatments.length > 0 ? (
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                TRATAMENTOS PRESCRITOS
              </Typography>
              <Box
                sx={{
                  maxHeight: 420,
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#00A884",
                    borderRadius: "10px",
                  },
                }}
              >
                <Stack spacing={2}>
                  {treatments.map((treatment) => {
                    const isSigned = (treatment as any).signed;
                    
                    return (
                      <Card
                        key={treatment.id}
                        sx={{
                          border: isSigned ? "2px solid #00A884" : "1px solid #e0e0e0",
                          transition: "all 0.2s ease",
                          bgcolor: isSigned ? "rgba(0, 168, 132, 0.02)" : "transparent",
                          "&:hover": {
                            borderColor: "#00A884",
                            boxShadow: "0 4px 12px rgba(0, 168, 132, 0.15)",
                          },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="h6" fontWeight={600} color="#00A884">
                                  {treatment.name}
                                </Typography>
                                {isSigned && (
                                  <CheckCircle sx={{ fontSize: 20, color: "#00A884" }} />
                                )}
                              </Stack>
                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(treatment)}
                                  disabled={isSigned}
                                  sx={{
                                    color: isSigned ? "#cccccc" : "#666",
                                    "&:hover": {
                                      color: isSigned ? "#cccccc" : "#00A884",
                                      bgcolor: isSigned ? "transparent" : "rgba(0, 168, 132, 0.08)",
                                    },
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(treatment.id)}
                                  disabled={isSigned}
                                  sx={{
                                    color: isSigned ? "#cccccc" : "#666",
                                    "&:hover": {
                                      color: isSigned ? "#cccccc" : "#f44336",
                                      bgcolor: isSigned ? "transparent" : "rgba(244, 67, 54, 0.08)",
                                    },
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>

                          <Divider />

                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              Posologia:
                            </Typography>
                            <Typography variant="body2">
                              {treatment.dosage}
                            </Typography>
                          </Box>

                          {treatment.description && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                              >
                                Observações:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {treatment.description}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "#fafafa",
                border: "1px dashed #e0e0e0",
                borderRadius: 2,
                minHeight: 420,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Medication sx={{ fontSize: 64, color: "#bdbdbd", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum tratamento adicionado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Preencha os campos ao lado e clique em "Adicionar Tratamento"
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, fontStyle: "italic" }}
              >
                💡 Dica: Use a tecla Tab para navegar entre os campos
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>

    {/* Modal de histórico — só exibido quando há histórico */}
    {treatmentsHistory.length > 0 && (
      <HistoryModal
        open={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false })}
        title="Histórico - Tratamentos Prescritos"
        history={treatmentsHistory}
      />
    )}
  </>
  );
};

export default TreatmentsSection;

